import { Observable, of, throwError, Subject } from "rxjs";
import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { ApiRoutes } from "../../../config/api-routes";
import { catchError, delay, map, tap, mergeMap } from "rxjs/internal/operators";
import { toISOString } from "src/app/helpers/dateFormatters";
import { ActionDetail } from "../model/actionDetail";
import { ActionSummary } from "../model/actionSummary";
import { ActionStatusType } from "../enums/enums";

export type JobListItem = {
  type: string;
  location: string;
  element: string;
  numberOfIssuesLinked: number;
  instructions: string;
  id: string;
  dueDate: Date;
  title: string;
};

export type JobsListElement = {
  buildingLabel: string;
  jobs: JobListItem[];
  id: string;
};


@Injectable()
export class JobService {
  public updated$: Subject<any> = new Subject<any>();

  constructor(private httpClient: HttpClient, private apiRoutes: ApiRoutes) {}

  public getJobs(httpParams: HttpParams): Observable<ActionSummary[]> {
    return this.httpClient
      .get<any>(this.apiRoutes.GET_JOBS, { params: httpParams })
      .pipe(
        map((response: any) => {
          return response.items;
        })
      );
  }

  //TODO this logic should be moved to the backend
  public getJobsByBuilding(
    httpParams: HttpParams
  ): Observable<JobsListElement[]> {
    return this.httpClient
      .get<any>(this.apiRoutes.GET_JOBS, { params: httpParams })
      .pipe(
        map((response: any) => {
          const jobsByBuilding: any = {};

          response.items.forEach(job => {
            const buildingLabel = job.issues[0].issueBuildingLabel;
            if (jobsByBuilding[buildingLabel]) {
              jobsByBuilding[buildingLabel].push(job);
            } else {
              jobsByBuilding[buildingLabel] = [job];
            }
          });
          return Object.entries(jobsByBuilding).map((buildingGroup: any[]) => {
            const [label, jobs] = buildingGroup;
            return {
              buildingLabel: label,
              id: jobs[0].id,
              jobs: jobs.map((job: any) => ({
                type: job.type,
                location: job.issues[0].issueLocationPath,
                element: job.issues[0].elementStructureLabel,
                numberOfIssuesLinked: job.issues.length,
                title: job.issues[0].issueTitle,
                id: job.id,
                dueDate: new Date(job.dueDate),
                instructions: job.instructions
              }))
            } as any;
          });
        }),
        catchError(error => {
          return of([]);
        })
      );
  }

  public getJobDetails(jobId: string): Observable<ActionDetail> {
    return this.httpClient
      .get<ActionDetail>(
        this.apiRoutes.getRouteWithInjectedParams(
          this.apiRoutes.GET_JOB_DETAIL,
          [jobId]
        )
      )
      .pipe(
        catchError(error => {
          return of({} as ActionDetail);
        })
      );
  }

  public acceptJob(jobId: string, scheduledOn: Date) {
    return this.httpClient
      .post<any>(
        this.apiRoutes.getRouteWithInjectedParams(this.apiRoutes.ACCEPT_JOB, [
          jobId
        ]), {scheduledOn: scheduledOn}
      )
      .pipe(
        tap(success => {
          if (success) {
            this.updated$.next(null);
          }
        }),
        catchError(error => {
          return of(false);
        })
      );
  }

  public acceptAllJobs(jobIds: string[]){
    return this.httpClient.post<any>(this.apiRoutes.ACCEPT_ALL_JOB, jobIds).pipe(tap(success => {
      if(success){
        this.updated$.next(null);
      }
    }),
    catchError(error => {
      return of(false);
    }));
  }

  public executeJob(
    jobId: string,
    feedback: string,
    multimediaFiles: File[]
  ): Observable<boolean> {
    const HttpUploadOptions = {
      headers: new HttpHeaders({ Accept: "application/json" }),
      observe: "response"
    };
    const formData = new FormData();
    multimediaFiles.forEach(file => {
      formData.append("files", file, file.name);
    });
    formData.append("actionCompleteRequest.feedback", feedback);
    formData.append("actionCompleteRequest.status",  ActionStatusType.FINISHED);

    return this.httpClient
      .post<any>(
        this.apiRoutes.getRouteWithInjectedParams(this.apiRoutes.COMPLETE_JOB, [
          jobId
        ]),
        formData,
        HttpUploadOptions as any
      )
      .pipe(
        map((response: any) => response.ok),
        tap(success => {
          if (success) {
            this.updated$.next(null);
          }
        }),
        catchError(error => {
          return of(false);
        })
      );
  }

  public declineJob(
    jobId: string,
    feedback: string,
    multimedia: any[]
  ): Observable<boolean> {
    const HttpUploadOptions = {
      headers: new HttpHeaders({ Accept: "application/json" }),
      observe: "response"
    };
    const formData = new FormData();
    multimedia.forEach(file => {
      formData.append("files", file, file.name);
    });
    formData.append("actionCompleteRequest.feedback", feedback);
    formData.append("actionCompleteRequest.status", ActionStatusType.DECLINED);

    return this.httpClient
      .post<any>(
        this.apiRoutes.getRouteWithInjectedParams(this.apiRoutes.COMPLETE_JOB, [
          jobId
        ]),
        formData,
        HttpUploadOptions as any
      )
      .pipe(
        map((response: any) => response.ok),
        tap(success => {
          if (success) {
            this.updated$.next(null);
          }
        }),
        catchError(error => {
          return of(false);
        })
      );
  }

  public declineAllJobs(jobIds: string[]){
    return this.httpClient.post<any>(this.apiRoutes.COMPLETE_ALL_JOB, jobIds).pipe(tap(success => {
      if(success){
        this.updated$.next(null);
      }
    }),
    catchError(error => {
      return of(false);
    }));
  }
}
